import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentSize } from '../types';

export interface TreeNode {
  id: string;
  label: string;
  icon?: string;
  children?: TreeNode[];
  expanded?: boolean;
  selected?: boolean;
  disabled?: boolean;
  data?: any;
}

@Component({
  selector: 'app-tree-view',
  imports: [CommonModule],
  template: `
    <div [class]="treeClasses" [attr.role]="'tree'">
      @for (node of nodes(); track node.id) {
        <div [class]="getNodeClasses(node)">
          <div 
            class="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
            (click)="toggleNode(node)"
            [attr.aria-expanded]="node.expanded"
            [attr.aria-selected]="node.selected">
            @if (node.children && node.children.length > 0) {
              <svg 
                class="w-4 h-4 transition-transform duration-200"
                [class.rotate-90]="node.expanded"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            } @else {
              <span class="w-4"></span>
            }
            @if (node.icon) {
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="node.icon" />
              </svg>
            }
            <span [class]="getLabelClasses(node)">{{ node.label }}</span>
          </div>
          @if (node.children && node.children.length > 0 && node.expanded) {
            <div class="ml-6">
              @for (child of node.children; track child.id) {
                <div [class]="getNodeClasses(child)">
                  <div 
                    class="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    (click)="toggleNode(child)"
                    [attr.aria-expanded]="child.expanded"
                    [attr.aria-selected]="child.selected">
                    @if (child.children && child.children.length > 0) {
                      <svg 
                        class="w-4 h-4 transition-transform duration-200"
                        [class.rotate-90]="child.expanded"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    } @else {
                      <span class="w-4"></span>
                    }
                    @if (child.icon) {
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="child.icon" />
                      </svg>
                    }
                    <span [class]="getLabelClasses(child)">{{ child.label }}</span>
                  </div>
                  @if (child.children && child.children.length > 0 && child.expanded) {
                    <div class="ml-6">
                      @for (grandchild of child.children; track grandchild.id) {
                        <div [class]="getNodeClasses(grandchild)">
                          <div 
                            class="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                            (click)="toggleNode(grandchild)"
                            [attr.aria-expanded]="grandchild.expanded"
                            [attr.aria-selected]="grandchild.selected">
                            @if (grandchild.icon) {
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="grandchild.icon" />
                              </svg>
                            }
                            <span [class]="getLabelClasses(grandchild)">{{ grandchild.label }}</span>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: []
})
export class TreeViewComponent {
  nodes = input<TreeNode[]>([]);
  size = input<ComponentSize>('md');
  selectable = input<boolean>(true);
  multiple = input<boolean>(false);

  nodeSelect = output<TreeNode>();

  toggleNode(node: TreeNode): void {
    if (node.disabled) return;
    
    if (node.children && node.children.length > 0) {
      node.expanded = !node.expanded;
    }
    
    if (this.selectable()) {
      if (this.multiple()) {
        node.selected = !node.selected;
      } else {
        this.deselectAll(this.nodes());
        node.selected = true;
      }
      this.nodeSelect.emit(node);
    }
  }

  private deselectAll(nodes: TreeNode[]): void {
    nodes.forEach(node => {
      node.selected = false;
      if (node.children) {
        this.deselectAll(node.children);
      }
    });
  }

  get treeClasses(): string {
    return 'space-y-1';
  }

  getNodeClasses(node: TreeNode): string {
    const classes = ['tree-node'];
    if (node.selected) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20');
    }
    return classes.join(' ');
  }

  getLabelClasses(node: TreeNode): string {
    const sizeMap = {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl'
    };
    
    const classes = [sizeMap[this.size()]];
    if (node.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('text-gray-900', 'dark:text-gray-100');
    }
    return classes.join(' ');
  }
}

